import { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, X, ChevronLeft } from "lucide-react";

export const OP_LABELS = {
  in: "ist", not_in: "ist nicht", exists: "vorhanden", not_exists: "fehlt",
  contains: "enthält (langsam)", not_contains: "enthält nicht (langsam)", starts_with: "beginnt mit", equals: "ist genau", not_equals: "ist nicht genau",
  gte: "≥", lte: "≤", eq: "=", is_true: "ja", is_false: "nein",
};
const NEGATIVE_OPS = new Set(["not_in", "not_exists", "not_contains", "not_equals", "is_false"]);
const VALUELESS_OPS = new Set(["exists", "not_exists", "is_true", "is_false"]);

export const VALUE_LABELS = {
  eol: "ohne Sicherheitsupdates", eol_soon: "Support endet bald", supported: "unterstützt", unknown: "unbekannt",
  done: "live", pending: "wartend", crawling: "läuft", error: "Fehler", rejected: "inaktiv",
  blocked: "blockiert (Bot-Schutz)", redirect: "Weiterleitung", skipped: "übersprungen", researching: "läuft",
  found: "gefunden", not_found: "nicht gefunden", ambiguous: "mehrdeutig",
  shared: "Shared Hosting", cloud: "Cloud/VPS", managed: "Managed", cdn: "CDN", isp: "Provider", platform: "Plattform",
};

export const PRESETS = [
  { label: "Drupal ohne Sicherheitsupdates", conditions: [
    { field: "cms", op: "in", value: ["Drupal"] }, { field: "drupal_support", op: "in", value: ["eol"] }] },
  { label: "Drupal-Support endet bald", conditions: [
    { field: "cms", op: "in", value: ["Drupal"] }, { field: "drupal_support", op: "in", value: ["eol_soon"] }] },
  { label: "PHP ohne Sicherheitsupdates", conditions: [{ field: "php_support", op: "in", value: ["eol"] }] },
  { label: "Lead-Score ab 50", conditions: [{ field: "lead_score", op: "gte", value: 50 }] },
  { label: "Kleinst- & Kleinunternehmen", conditions: [{ field: "company_size", op: "in", value: ["Kleinst", "Klein"] }] },
  { label: "Mit Telefonnummer", conditions: [{ field: "phone", op: "exists" }] },
  { label: "Noch nicht recherchiert", conditions: [{ field: "research_status", op: "not_in", value: ["done"] }] },
  { label: "Nicht in Liquidation (Register)", conditions: [{ field: "hr_liquidation", op: "is_false" }] },
];

const fmt = (n) => Number(n).toLocaleString("de-DE");

export function describeCondition(c, fieldsByKey) {
  const f = fieldsByKey[c.field];
  const label = f?.label || c.field;
  const op = OP_LABELS[c.op] || c.op;
  if (VALUELESS_OPS.has(c.op)) return `${label}: ${op}`;
  const vals = Array.isArray(c.value) ? c.value : [c.value];
  const shown = vals.slice(0, 3).map((v) => VALUE_LABELS[v] || v).join(", ") + (vals.length > 3 ? ` +${vals.length - 3}` : "");
  return `${label} ${op} ${shown}`;
}

function ValueEditor({ field, op, value, setValue, options }) {
  const [q, setQ] = useState("");
  if (VALUELESS_OPS.has(op)) return <p className="text-xs text-zinc-500">Kein Wert nötig.</p>;
  const t = field.type;
  if (t === "enum" || t === "list" || t === "support") {
    const counts = options || {};
    const keys = field.options || Object.keys(counts);
    const filtered = keys.filter((k) => String(VALUE_LABELS[k] || k).toLowerCase().includes(q.toLowerCase())).slice(0, 150);
    const selected = new Set(Array.isArray(value) ? value.map(String) : []);
    const toggle = (k) => {
      const next = new Set(selected);
      next.has(String(k)) ? next.delete(String(k)) : next.add(String(k));
      setValue(Array.from(next));
    };
    return (
      <div className="space-y-2">
        {keys.length > 8 && (
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Werte durchsuchen…" className="h-8 text-xs bg-zinc-950 border-zinc-800" />
        )}
        <div className="max-h-56 overflow-y-auto space-y-0.5 pr-1">
          {filtered.length === 0 && <p className="text-xs text-zinc-500 py-2">Keine Werte (Statistik wird im Hintergrund berechnet).</p>}
          {filtered.map((k) => (
            <label key={k} className="flex items-center gap-2 px-2 py-1 rounded-sm hover:bg-zinc-800/60 cursor-pointer text-xs text-zinc-200">
              <Checkbox checked={selected.has(String(k))} onCheckedChange={() => toggle(k)} />
              <span className="flex-1 truncate">{VALUE_LABELS[k] || k}</span>
              {counts[k] != null && <span className="text-zinc-500 font-mono text-[10px]">{fmt(counts[k])}</span>}
            </label>
          ))}
        </div>
        {t !== "support" && (
          <Input placeholder="Eigener Wert + Enter" className="h-8 text-xs bg-zinc-950 border-zinc-800"
                 onKeyDown={(e) => { if (e.key === "Enter" && e.currentTarget.value.trim()) { toggle(e.currentTarget.value.trim()); e.currentTarget.value = ""; } }} />
        )}
      </div>
    );
  }
  if (t === "number") {
    return <Input type="number" value={value ?? ""} onChange={(e) => setValue(e.target.value)} className="h-8 text-xs bg-zinc-950 border-zinc-800" autoFocus />;
  }
  if (t === "date") {
    return <Input type="date" value={value ?? ""} onChange={(e) => setValue(e.target.value)} className="h-8 text-xs bg-zinc-950 border-zinc-800" autoFocus />;
  }
  return <Input value={value ?? ""} onChange={(e) => setValue(e.target.value)} placeholder="Text" className="h-8 text-xs bg-zinc-950 border-zinc-800" autoFocus />;
}

function ConditionEditor({ fields, options, initial, onSave, onCancel }) {
  const [fieldKey, setFieldKey] = useState(initial?.field || null);
  const field = fields.find((f) => f.key === fieldKey);
  const [op, setOp] = useState(initial?.op || null);
  const [value, setValue] = useState(initial?.value ?? null);

  const groups = useMemo(() => {
    const g = {};
    fields.forEach((f) => { (g[f.group] = g[f.group] || []).push(f); });
    return g;
  }, [fields]);

  if (!field) {
    return (
      <Command className="bg-transparent">
        <CommandInput placeholder="Feld suchen…" />
        <CommandList className="max-h-80">
          <CommandEmpty>Kein Feld gefunden.</CommandEmpty>
          {Object.entries(groups).map(([group, items]) => (
            <CommandGroup key={group} heading={group}>
              {items.map((f) => (
                <CommandItem key={f.key} value={`${group} ${f.label}`} onSelect={() => { setFieldKey(f.key); setOp(f.ops[0]); setValue(null); }}>
                  {f.label}
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
        </CommandList>
      </Command>
    );
  }

  const valid = VALUELESS_OPS.has(op) || (Array.isArray(value) ? value.length > 0 : value !== null && value !== "");
  return (
    <div className="p-3 space-y-3">
      <button className="flex items-center gap-1 text-xs text-zinc-400 hover:text-white" onClick={() => setFieldKey(null)}>
        <ChevronLeft className="w-3 h-3" /> {field.label}
      </button>
      <div className="flex flex-wrap gap-1">
        {field.ops.map((o) => (
          <button key={o} onClick={() => setOp(o)}
            className={`px-2 py-1 rounded-sm text-[11px] border transition-colors ${op === o
              ? (NEGATIVE_OPS.has(o) ? "bg-red-500/15 border-red-500/40 text-red-300" : "bg-blue-500/15 border-blue-500/40 text-blue-300")
              : "border-zinc-700 text-zinc-400 hover:text-white"}`}>
            {OP_LABELS[o]}
          </button>
        ))}
      </div>
      <ValueEditor field={field} op={op} value={value} setValue={setValue} options={options?.[field.key]} />
      <div className="flex justify-end gap-2 pt-1">
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={onCancel}>Abbrechen</Button>
        <Button size="sm" className="h-7 text-xs bg-blue-600 hover:bg-blue-500 text-white" disabled={!valid}
                onClick={() => onSave({ field: field.key, op, ...(VALUELESS_OPS.has(op) ? {} : { value: field.type === "number" ? Number(value) : value }) })}>
          Übernehmen
        </Button>
      </div>
    </div>
  );
}

export function FilterBuilder({ schema, conditions, onChange }) {
  const [open, setOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const fields = useMemo(() => schema?.fields || [], [schema]);
  const fieldsByKey = useMemo(() => Object.fromEntries(fields.map((f) => [f.key, f])), [fields]);

  const save = (c) => {
    const next = [...conditions];
    if (editIndex !== null) next[editIndex] = c; else next.push(c);
    onChange(next);
    setOpen(false);
    setEditIndex(null);
  };
  const remove = (i) => onChange(conditions.filter((_, idx) => idx !== i));

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {conditions.map((c, i) => (
        <span key={i}
          className={`group inline-flex items-center gap-1 pl-2 pr-1 h-7 rounded-sm border text-[11px] cursor-pointer ${NEGATIVE_OPS.has(c.op)
            ? "border-red-500/30 bg-red-500/10 text-red-200" : "border-blue-500/30 bg-blue-500/10 text-blue-200"}`}
          onClick={() => { setEditIndex(i); setOpen(true); }}>
          {describeCondition(c, fieldsByKey)}
          <button aria-label="Filter entfernen" className="p-0.5 rounded-sm hover:bg-white/10"
                  onClick={(e) => { e.stopPropagation(); remove(i); }}>
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
      <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditIndex(null); }}>
        <PopoverTrigger asChild>
          <Button size="sm" variant="outline" className="h-7 text-[11px] rounded-sm border-zinc-700 text-zinc-300" data-testid="add-filter-btn"
                  onClick={() => setEditIndex(null)}>
            <Plus className="w-3 h-3 mr-1" /> Filter
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 p-0 bg-zinc-900 border-zinc-800">
          {open && (
            <ConditionEditor
              key={editIndex ?? "new"}
              fields={fields}
              options={schema?.options}
              initial={editIndex !== null ? conditions[editIndex] : null}
              onSave={save}
              onCancel={() => { setOpen(false); setEditIndex(null); }}
            />
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
